import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, TrendingUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const ROASCalculator = () => {
  const [adSpend, setAdSpend] = useState(1000);
  const [revenue, setRevenue] = useState(5000);
  const [roas, setRoas] = useState(0);

  useEffect(() => {
    calculateROAS();
  }, [adSpend, revenue]);

  const calculateROAS = () => {
    const calculatedRoas = revenue / adSpend;
    setRoas(parseFloat(calculatedRoas.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/roas-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>ROAS Calculator: Measure Ad Spend Efficiency | Free Tool</title>
        <meta name="description" content="Calculate your Return on Ad Spend (ROAS) instantly. Compare with industry benchmarks, get optimization tips, and improve your advertising efficiency." />
        <meta name="keywords" content="ROAS calculator, return on ad spend, advertising metrics, ad performance, campaign efficiency" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free ROAS Calculator: Optimize Ad Spend Returns" />
        <meta property="og:description" content="Calculate your Return on Ad Spend instantly. Get actionable insights to improve campaign efficiency and boost returns." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free ROAS Calculator: Optimize Ad Spend Returns" />
        <meta property="twitter:description" content="Calculate your Return on Ad Spend instantly. Get actionable insights to improve campaign efficiency and boost returns." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Return on Ad Spend Calculator",
            "description": "Free calculator to measure and optimize advertising spend efficiency",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate ROAS",
              "Industry Benchmarks",
              "Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Return on Ad Spend",
              "description": "The revenue generated for every dollar spent on advertising, measuring campaign efficiency."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Media Buyers, Digital Marketers, Campaign Managers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Return on Ad Spend",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total ad spend"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input revenue generated"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your ROAS and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Return on Ad Spend (ROAS) Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Ad Spend</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={adSpend}
                  onChange={(e) => setAdSpend(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Revenue Generated</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={revenue}
                  onChange={(e) => setRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {roas}:1
            <TrendingUp className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Return on Ad Spend (ROAS)</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Return on Ad Spend (ROAS) is {roas}:1. This means that for every dollar spent on advertising, you're generating ${roas} in revenue.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry ROAS Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>E-commerce: 4:1 - 6:1</li>
          <li>Retail: 3:1 - 5:1</li>
          <li>B2B: 2:1 - 4:1</li>
          <li>SaaS: 3:1 - 5:1</li>
          <li>Travel: 4:1 - 7:1</li>
          <li>Healthcare: 2:1 - 4:1</li>
          <li>Finance: 3:1 - 5:1</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">ROAS Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Optimize targeting</li>
          <li>Improve ad creatives</li>
          <li>Enhance landing pages</li>
          <li>Use retargeting</li>
          <li>Refine bid strategies</li>
          <li>Focus on keywords</li>
          <li>Test campaigns</li>
          <li>Consider lifetime value</li>
          <li>Monitor competitors</li>
          <li>Analyze customer data</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Performance Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Click-Through Rate (CTR)</li>
          <li>Conversion Rate</li>
          <li>Average Order Value</li>
          <li>Customer Acquisition Cost</li>
          <li>Customer Lifetime Value</li>
          <li>Ad Position</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Better targeting: +30% ROAS</li>
          <li>Creative optimization: +25% ROAS</li>
          <li>Landing page testing: +20% ROAS</li>
          <li>Bid optimization: +15% ROAS</li>
          <li>Audience segmentation: +35% ROAS</li>
        </ul>
      </div>
    </div>
  );
};

export default ROASCalculator;
